import { useEffect, useState } from "react";
import client from "../api/client";
import { useAuth } from "../context/AuthContext";
import { formatDateShort, formatDateFull, mapsUrl, CZ_MONTHS } from "../utils/format";
import { Phone, Mail, Navigation, LogOut, CalendarDays, List, MapPin, User, ChevronLeft, ChevronRight, X } from "lucide-react";

function EventCard({ w, muted }) {
  return (
    <div className={`border border-border rounded-xl p-4 bg-card ${muted ? "opacity-60" : ""}`} data-testid={`portal-event-${w.id}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-heading font-semibold text-base">{formatDateShort(w.date)}</div>
        <div className="text-sm text-muted-foreground">{w.time_from}–{w.time_to}</div>
      </div>
      <div className="font-medium mt-1">{w.school_name}</div>
      <div className="text-sm text-muted-foreground">{w.program_name}</div>
      <div className="text-sm text-muted-foreground flex items-start gap-1 mt-1"><MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {w.address}</div>
      {w.contact_person && <div className="text-sm mt-1"><span className="text-muted-foreground">Kontakt: </span>{w.contact_person}</div>}
      {w.note && <div className="text-sm mt-1 text-muted-foreground">„{w.note}"</div>}
      {!muted && (
        <div className="grid grid-cols-3 gap-2 mt-3">
          <a href={w.school_phone ? `tel:${w.school_phone}` : undefined} className={`flex items-center justify-center gap-1.5 h-11 rounded-lg text-sm font-medium ${w.school_phone ? "bg-emerald-600 text-white" : "bg-muted text-muted-foreground pointer-events-none"}`} data-testid={`call-${w.id}`}>
            <Phone className="h-4 w-4" /> Zavolat
          </a>
          <a href={w.school_email ? `mailto:${w.school_email}` : undefined} className={`flex items-center justify-center gap-1.5 h-11 rounded-lg text-sm font-medium ${w.school_email ? "bg-secondary" : "bg-muted text-muted-foreground pointer-events-none"}`} data-testid={`email-${w.id}`}>
            <Mail className="h-4 w-4" /> E-mail
          </a>
          <a href={mapsUrl(w.address)} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 h-11 rounded-lg text-sm font-medium bg-primary text-primary-foreground" data-testid={`nav-${w.id}`}>
            <Navigation className="h-4 w-4" /> Navigace
          </a>
        </div>
      )}
    </div>
  );
}

export default function LectorPortal() {
  const { user, logout } = useAuth();
  const [workshops, setWorkshops] = useState([]);
  const [tab, setTab] = useState("list");
  const [current, setCurrent] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [selected, setSelected] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    client.get("/workshops", { params: { filter: "all" } }).then((res) => setWorkshops(res.data));
  }, []);

  const upcoming = workshops.filter((w) => w.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  const past = workshops.filter((w) => w.date < today).sort((a, b) => b.date.localeCompare(a.date));

  const byDate = {};
  workshops.forEach((w) => { (byDate[w.date] = byDate[w.date] || []).push(w); });
  const first = new Date(current.y, current.m, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(current.y, current.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const dateKey = (d) => `${current.y}-${String(current.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  return (
    <div className="min-h-screen bg-muted/30 pb-6">
      <header className="sticky top-0 z-10 bg-background border-b border-border px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-primary text-primary-foreground grid place-items-center font-heading font-bold text-sm">L</div>
          <span className="font-heading font-semibold text-sm">Lektorský portál</span>
        </div>
        <button onClick={logout} data-testid="portal-logout" className="p-2 text-muted-foreground"><LogOut className="h-5 w-5" /></button>
      </header>

      <div className="px-4 pt-4">
        <div className="flex items-center gap-2 mb-4 text-sm">
          <User className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{user?.name}</span>
        </div>
        <div className="flex rounded-lg border border-border overflow-hidden mb-4">
          <button onClick={() => setTab("list")} data-testid="portal-tab-list" className={`flex-1 h-10 text-sm flex items-center justify-center gap-1.5 ${tab === "list" ? "bg-primary text-primary-foreground" : ""}`}><List className="h-4 w-4" /> Akce</button>
          <button onClick={() => setTab("calendar")} data-testid="portal-tab-calendar" className={`flex-1 h-10 text-sm flex items-center justify-center gap-1.5 ${tab === "calendar" ? "bg-primary text-primary-foreground" : ""}`}><CalendarDays className="h-4 w-4" /> Kalendář</button>
        </div>
      </div>

      {tab === "list" ? (
        <div className="px-4 space-y-6 max-w-lg mx-auto">
          <div>
            <h2 className="font-heading font-semibold mb-2">Nadcházející akce</h2>
            <div className="space-y-3">
              {upcoming.map((w) => <EventCard key={w.id} w={w} />)}
              {upcoming.length === 0 && <p className="text-sm text-muted-foreground">Žádné nadcházející akce</p>}
            </div>
          </div>
          {past.length > 0 && (
            <div>
              <h2 className="font-heading font-semibold mb-2 text-muted-foreground">Proběhlé akce</h2>
              <div className="space-y-3">{past.map((w) => <EventCard key={w.id} w={w} muted />)}</div>
            </div>
          )}
        </div>
      ) : (
        <div className="px-4 max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setCurrent((c) => (c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 }))} className="p-2 rounded-md bg-background border border-border"><ChevronLeft className="h-4 w-4" /></button>
            <span className="font-medium">{CZ_MONTHS[current.m]} {current.y}</span>
            <button onClick={() => setCurrent((c) => (c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 }))} className="p-2 rounded-md bg-background border border-border"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1">
            {["Po", "Út", "St", "Čt", "Pá", "So", "Ne"].map((d) => <div key={d} className="text-center text-[11px] text-muted-foreground py-1">{d}</div>)}
            {cells.map((d, i) => {
              const events = d ? byDate[dateKey(d)] || [] : [];
              return (
                <button key={i} disabled={!events.length} onClick={() => events.length && setSelected(events[0])} className={`aspect-square rounded-md text-sm flex flex-col items-center justify-center ${events.length ? "bg-primary text-primary-foreground font-medium" : d ? "bg-background border border-border" : ""}`}>
                  {d && <span>{d}</span>}
                  {events.length > 1 && <span className="text-[9px]">+{events.length}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onMouseDown={() => setSelected(null)}>
          <div className="bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-5" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-heading font-semibold">{formatDateFull(selected.date)}</h3>
              <button onClick={() => setSelected(null)}><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>
            <EventCard w={selected} />
          </div>
        </div>
      )}
    </div>
  );
}
